import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { submitAccessRequest } from "@/lib/auth/auth.functions";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/request-access")({
  head: () => ({ meta: [{ title: "Request access · CARIO INTEL" }] }),
  component: RequestAccessPage,
});

function RequestAccessPage() {
  const submit = useServerFn(submitAccessRequest);
  const [form, setForm] = useState({ full_name: "", email: "", organization: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await submit({ data: { ...form, organization: form.organization || null } });
      setDone(true);
    } catch (e: any) {
      toast.error(e?.message || "Could not submit request");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid-bg bg-background text-foreground flex items-center justify-center px-4 py-12">
      <Toaster />
      <div className="w-full max-w-lg">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan hover:text-foreground">
            Have access? Sign in →
          </Link>
        </div>
        <div className="glass-strong rounded-xl p-8 ring-glow">
          {done ? (
            <div className="text-center py-6">
              <CheckCircle2 className="mx-auto h-12 w-12 text-success" />
              <h1 className="mt-4 font-display text-2xl">Request received</h1>
              <p className="mt-2 text-sm text-muted-foreground">
                An administrator will review your request and provision credentials if approved.
              </p>
              <Link to="/" className="mt-6 inline-flex text-cyan hover:underline text-sm">← Back to home</Link>
            </div>
          ) : (
            <>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Access vetting</p>
              <h1 className="mt-2 font-display text-3xl">Request platform access</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                CARIO INTEL is a restricted environment. Submit your details for review.
              </p>
              <form onSubmit={onSubmit} className="mt-6 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="full_name">Full name</Label>
                  <Input id="full_name" required maxLength={120}
                    value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="email">Work email</Label>
                  <Input id="email" type="email" required maxLength={255}
                    value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="organization">Organization</Label>
                  <Input id="organization" maxLength={160}
                    value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reason">Why you need access</Label>
                  <Textarea id="reason" required minLength={10} maxLength={2000} rows={5}
                    value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                    placeholder="Describe your role, use-case, and the type of investigations you plan to run." />
                </div>
                <Button type="submit" disabled={busy} className="w-full">
                  {busy ? "Submitting…" : "Submit request"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}